import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Typography from '../Typography';
import { theme } from '../../theme';
import EllipsisVerticalIcon from '../Icons/EllipsisVerticalIcon';
import { getVehicleIcon, getDefaultIconProps } from '../../utils/vehicleIcons';
import ReactionGestureSolidIcon from '../Icons/ReactionGestureSolidIcon';
import ReactionGestureOutlineIcon from '../Icons/ReactionGestureOutlineIcon';
import axiosClient from '../../api/axiosClient';
import { useTranslation } from 'react-i18next';

/**
 * Este componente recibe:
 * - vehicle: Objeto con la información del vehículo (brand, model, etc.)
 * - isOwnGarage: booleano que indica si el garage pertenece al usuario (para mostrar acciones)
 * - openEditModal: función para abrir el modal de edición de vehículo
 * - handleDeleteVehicle: función para eliminar el vehículo
 */
function VehicleCard({ vehicle, isOwnGarage, openEditModal, handleDeleteVehicle, initialLiked, likesReady = true, originContext }) {
  const { t } = useTranslation('userProfile')
  // Estado local para controlar la visibilidad del menú
  const [showMenu, setShowMenu] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const menuRef = useRef(null);

  // Obtener el icono correspondiente al tipo de vehículo
  const VehicleIconComponent = getVehicleIcon(vehicle?.vehicleType);
  // const iconProps = getDefaultIconProps(vehicle?.vehicleType);
  const iconProps = {
    ...getDefaultIconProps(theme.fill.brandMain),
    width: 64,  // Ancho personalizado
    height: 64  // Alto personalizado
  };

  // Toggle para abrir/cerrar el menú
  const toggleMenu = (event) => {
    event.stopPropagation();
    event.preventDefault();
    // console.log('VehicleCard: Menu toggle clicked, current showMenu:', showMenu);
    setShowMenu((prev) => !prev);
  };

  // Cierra el menú si el usuario hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Verificar que el clic no sea en un modal o elemento que deba mantener el menú
      if (menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !event.target.closest('[role="dialog"]') &&
        !event.target.closest('.modal-content') &&
        !event.target.closest('[data-modal]') &&
        !event.target.closest('.complete-profile') &&
        !event.target.closest('form') &&
        !event.target.closest('.modal') &&
        !event.target.closest('.toast') &&
        !event.target.closest('.Toastify__toast')) {
        // console.log('VehicleCard: Closing menu due to outside click');
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    const loadLikeStatus = async () => {
      if (isOwnGarage) return;
      if (typeof initialLiked === 'boolean') { setLiked(initialLiked); return; }
      try {
        const { data } = await axiosClient.get(`/api/vehicles/${vehicle._id}/like/status`);
        setLiked(Boolean(data?.liked));
      } catch (e) { }
    };
    if (vehicle?._id) loadLikeStatus();
  }, [vehicle?._id, isOwnGarage, initialLiked]);

  const toggleLike = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!vehicle?._id || likeLoading) return;
    setLikeLoading(true);
    try {
      if (liked) {
        await axiosClient.delete(`/api/vehicles/${vehicle._id}/like`);
        setLiked(false);
      } else {
        await axiosClient.post(`/api/vehicles/${vehicle._id}/like`);
        setLiked(true);
      }
    } catch (e) { }
    finally {
      setLikeLoading(false);
    }
  };

  return (
    <Vehicle>
      {vehicle?.image ? (
        <VehicleImage src={vehicle.image} alt={vehicle.brand} />
      ) : (
        <VehicleIconPlaceholder>
          <VehicleIconComponent {...iconProps} />
        </VehicleIconPlaceholder>
      )}
      <VehicleData>
        {vehicle.nickname ? (
          <>
            <Typography $variant="body-1-medium" color={theme.colors.inverseMain}>
              {vehicle?.brand}
              <Typography as="span" color={theme.colors.inverseStrong} style={{ marginLeft: '4px' }}>
                {vehicle?.model}
              </Typography>
            </Typography>
            <Typography $variant="title-5-semibold" color={theme.colors.inverseMain}>{vehicle.nickname}</Typography>
            <Typography $variant="body-3-medium" color={theme.colors.inverseStrong}>{vehicle.year}</Typography>
          </>
        ) : (
          <>
            <Typography $variant="body-1-medium" color={theme.colors.inverseMain}>
              {vehicle?.brand}
            </Typography>
            <Typography $variant="title-5-semibold" color={theme.colors.inverseMain}>{vehicle.model}</Typography>
            <Typography $variant="body-3-medium" color={theme.colors.inverseStrong}>{vehicle.year}</Typography>
          </>
        )}
      </VehicleData>


      {isOwnGarage && (
        <Dropdown>
          <MenuIcon onClick={toggleMenu} data-testid="vehicle-menu" type="button">
            <EllipsisVerticalIcon fill={theme.colors.inverseMain} />
          </MenuIcon>

          {/* Renderizamos el contenedor del menú si showMenu es true */}
          {showMenu && (
            <DropdownMenu ref={menuRef}>
              <MenuItem type="button" onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                console.log('VehicleCard: Edit button clicked for vehicle:', vehicle._id);
                setShowMenu(false);
                openEditModal(vehicle);
              }}>
                {/* Editar */}
                {t('garage.vehicle.edit')}
              </MenuItem>
              <MenuItem
                $danger
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  console.log('VehicleCard: Delete button clicked for vehicle:', vehicle._id);
                  setShowMenu(false);
                  handleDeleteVehicle(vehicle._id);
                }}
                data-testid="delete-vehicle"
                type="button"
              >
                {/* Eliminar */}
                {t('garage.vehicle.delete')}
              </MenuItem>
            </DropdownMenu>
          )}
        </Dropdown>
      )}
      {!isOwnGarage && likesReady && (
        <ReactionButton
          onClick={toggleLike}
          aria-pressed={liked}
          disabled={likeLoading}
          title={liked ? t('garage.vehicle.removeReaction') : t('garage.vehicle.reaction')}
          $active={liked}
          type="button"
        >
          {liked ? (
            <ReactionGestureSolidIcon width={20} height={20} fill={theme.fill.brandMain} />
          ) : (
            <ReactionGestureOutlineIcon width={20} height={20} fill={theme.colors.inverseMain} />
          )}
        </ReactionButton>
      )}
    </Vehicle>
  );
}

export default VehicleCard;

const Vehicle = styled.li`
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  overflow: hidden;
  position: relative;
  aspect-ratio: 4 / 3;
`;

const VehicleImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 8px;
  object-fit: cover;
`;

const VehicleIconPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 8px;
  background: ${({ theme }) => theme.fill.inverseWeak};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VehicleData = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: ${({ theme }) => theme.sizing.sm};
  padding-top: 88px;
  background: linear-gradient(180deg, rgba(16, 17, 15, 0.00) 0%, rgba(16, 17, 15, 0.01) 6.67%, rgba(16, 17, 15, 0.04) 13.33%, rgba(16, 17, 15, 0.08) 20%, rgba(16, 17, 15, 0.15) 26.67%, rgba(16, 17, 15, 0.23) 33.33%, rgba(16, 17, 15, 0.33) 40%, rgba(16, 17, 15, 0.44) 46.67%, rgba(16, 17, 15, 0.56) 53.33%, rgba(16, 17, 15, 0.67) 60%, rgba(16, 17, 15, 0.77) 66.67%, rgba(16, 17, 15, 0.85) 73.33%, rgba(16, 17, 15, 0.92) 80%, rgba(16, 17, 15, 0.96) 86.67%, rgba(16, 17, 15, 0.99) 93.33%, #10110F 100%);
`;

const Dropdown = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  flex-direction: column;
  align-items: flex-end;
`;

const MenuIcon = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.fill.inverseAlphaMain16};
  border: none;
  cursor: pointer;
  width: ${({ theme }) => theme.sizing.lg};
  height: ${({ theme }) => theme.sizing.lg};
  border-radius: ${({ theme }) => theme.radius.xs};
  transition: all 0.3s ease-in-out;

  img {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.fill.inverseAlphaMain24};
  }
`;

const ReactionButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.sizing.xs};
  right: ${({ theme }) => theme.sizing.xs};
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme, $active }) => ($active ? theme.fill.defaultMain : theme.fill.defaultAlphaMain50)};
  border: none;
  cursor: pointer;
  width: ${({ theme }) => theme.sizing.lg};
  height: ${({ theme }) => theme.sizing.lg};
  border-radius: ${({ theme }) => theme.radius.md};
  transition: all 0.2s ease-in-out;
  backdrop-filter: blur(4px);

  &:hover {
    background: ${({ theme, $active }) => ($active ? theme.fill.defaultAlphaMain80 : theme.fill.defaultAlphaMain60)};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const DropdownMenu = styled.div`
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.06);
  z-index: 999;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.xxs};
`;

const MenuItem = styled.button`
  background: transparent;
  border: none;
  font-size: 16px;
  padding: ${({ theme }) => theme.sizing.xs};
  text-align: left;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.xxs};
  color: ${(props) => (props.$danger ? theme.colors.errorMain : theme.colors.defaultMain)};
  transition: all 0.3s ease-in-out;

  &:hover {
    // background-color: ${({ theme }) => theme.fill.defaultWeak};
    background-color: ${(props) => (props.$danger ? theme.fill.elserrorAlphaMain16 : theme.fill.defaultWeak)};
  }
`;
